import React from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Footer } from '../../features/footer/Footer';
import { Header } from '../../features/header/Header';
import {
  Row,
  Col,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import { withRouter } from 'react-router-dom';
import {
  withAuthentication,
  getThoughtSpotToken,
  getJittoEmbedToken,
} from '../../common/authentication';
import {
  jittoEmbedIdFor,
  mountJittoDashboard,
} from '../../features/dashboards/jittoEmbed';
import { createCurrencyFilter, DEFAULT_CURRENCY } from './currencyFilter';
import {
  PinboardEmbed,
  AuthType,
  init,
  RuntimeFilterOp,
  Action,
} from '@thoughtspot/visual-embed-sdk';
import config from '../../config';
import { dashboardTitles } from './titles';
import {
  SPOTTER_TEXT_CUSTOMIZATION,
  SPOTTER_ICON_SPRITE_URL,
} from '../../common/constants';
//import messages from './dashboardMessages';

const IFrameContainer = styled.div``;
// The embedded SDK puts its own <iframe> in here. A tall dashboard scrolls inside the
// frame, so its filter bar stays on screen. The container has the room the window
// leaves; a short dashboard's container ends where its frame does (onSizeChanged), so
// it leaves no gap below it, and max-height holds that height to the window too.
export const JittoContainer = styled.div`
  width: 100%;
  height: calc(100vh - 220px);
  max-height: calc(100vh - 220px);
  & iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;
const InlineDropdown = styled(UncontrolledDropdown)`
  display: inline-block;
`;

export class DashboardPage extends React.Component {
  constructor(props) {
    super(props);
    this.tsContainerRef = React.createRef();
    this.jittoContainerRef = React.createRef();
    this.embeddedVizId = null;
    this.jittoHandle = null;
    // The embedded dashboard's currency filter (ANS-664); a new one for each mount.
    this.currencyFilter = createCurrencyFilter({
      saved: props.currency,
      filtered: this.currencyFiltered(),
      log: console.error,
      onChange: () => this.forceUpdate(),
    });
  }

  componentDidMount() {
    if (!this.props.dashboards) {
      this.props.getDashboards();
    }

    // With Jitto Answer on, nothing on this page asks ThoughtSpot.
    if (!config.jittoEmbedActiveFlag) this.initThoughtSpot();

    // The dashboard waits for the preferences: it is synced once they load
    // (componentDidUpdate), never from what an earlier page left in the store (ANS-1118).
    this.props.getPreferences();
  }

  initThoughtSpot() {
    init({
      thoughtSpotHost: config.thoughtSpotHost,
      authType: AuthType.AuthServer,
      username: this.props.authState.username,
      getAuthToken: () =>
        new Promise((resolve, reject) => {
          getThoughtSpotToken(this.props.authState.accessToken).then(
            (tsinfo) => {
              resolve(tsinfo.thoughtSpotToken);
            },
          );
        }),
    });
  }

  componentDidUpdate(prevProps) {
    // Saved preferences loaded after the mount (a new `loaded` from
    // preferences/get) apply as a pick, even one repeating the last load. A
    // save's acknowledgement is never one, whatever currency it carries and
    // whenever it lands: that currency was picked here already.
    const { loaded } = this.props;
    const prevLoaded = prevProps.loaded;
    if (loaded && loaded.currency && (!prevLoaded || loaded.seq !== prevLoaded.seq)) {
      this.currencyFilter.pick(loaded.currency);
    }
    // ThoughtSpot renders once preferences settle, even before the dashboard
    // list arrives (or if it never does) (ANS-1118).
    if (!this.props.dashboards && loaded === prevLoaded) return;
    this.syncDashboard();
  }

  // The preferences have loaded (or failed, which means SGD) and no load is
  // outstanding (ANS-1118).
  preferencesLoaded() {
    const { loaded, requests } = this.props;
    return Boolean(loaded) && !(requests && requests.loading);
  }

  // A pick waits while a load could overwrite it or a save is unacknowledged (ANS-1118).
  pickerBusy() {
    const { requests } = this.props;
    return !this.preferencesLoaded() || Boolean(requests && requests.saving);
  }

  // With the flag on, Jitto Answer only: a viz_id with no embedded uuid shows a
  // notice. With it off, ThoughtSpot.
  jittoEmbedId() {
    const { viz_id } = this.props.match.params;
    return jittoEmbedIdFor(viz_id, config);
  }

  // Whether the dashboard is filtered by currency. ThoughtSpot: always, by a runtime
  // filter. Jitto: once its server and SDK apply host filters (config.jittoHostFilters,
  // ANS-801). Every Jitto dashboard has a Ccy filter, PM Overview too since ANS-852
  // (jitto-client-tpicap-tca af824291); Jitto refuses the whole frame for a filter name
  // the dashboard lacks (jitto-analyze#540).
  currencyFiltered() {
    return !config.jittoEmbedActiveFlag || Boolean(config.jittoHostFilters);
  }

  syncDashboard() {
    const { viz_id } = this.props.match.params;
    if (!config.jittoEmbedActiveFlag) {
      // Render once the saved currency is known (a load, or a failed one, which
      // means SGD): rendered before, the dashboard had no Ccy filter (ANS-1118).
      if (!this.preferencesLoaded()) return;
      this.renderDashboard(this.tsContainerRef.current, viz_id);
      return;
    }
    const embedId = this.jittoEmbedId();
    if (!embedId) {
      this.embeddedVizId = null;
      return;
    }
    // Mount once the saved currency is known (a load, or a failed one, which
    // means SGD): a currency set after the mount reloads the dashboard
    // (ANS-801, D4).
    if (!this.preferencesLoaded()) return;
    // The SDK owns re-renders; embed once per viz_id.
    if (this.embeddedVizId === viz_id) return;
    this.embeddedVizId = viz_id;
    const mountPoint = this.jittoContainerRef.current;
    if (!mountPoint) return;
    this.unmountJitto();
    // Back to the stylesheet's height until the new dashboard reports its own.
    mountPoint.style.height = '';
    // The dashboard renders in the user's currency from the first frame, and
    // the seam sends each later pick. @jitto/analyze-embed frames the dashboard,
    // refreshes its token and reports a failure once (a refusal, a refused
    // filter, or a refresh that failed for good); the page then shows its notice.
    this.currencyFilter = createCurrencyFilter({
      saved: this.props.currency,
      filtered: this.currencyFiltered(),
      log: console.error,
      onChange: () => this.forceUpdate(),
    });
    try {
      this.jittoHandle = mountJittoDashboard({
        embedId,
        mountPoint,
        requestToken: getJittoEmbedToken,
        embedOrigin: config.jittoEmbedOrigin,
        filters: this.currencyFilter.mount,
        onFailed: (event) => this.currencyFilter.mountFailed(event),
        onSizeChanged: ({ height }) => {
          if (this.embeddedVizId !== viz_id) return;
          // Only a frame shorter than the stylesheet's height gets a height of its
          // own: one that fills it keeps following the window (ANS-1112).
          mountPoint.style.height = '';
          if (Math.ceil(height) < mountPoint.clientHeight) {
            mountPoint.style.height = `${Math.ceil(height)}px`;
          }
        },
      });
      this.currencyFilter.attach(this.jittoHandle);
    } catch (err) {
      this.currencyFilter.mountFailed(err);
    }
    // Render from the new seam: the last one may have shown a failure.
    this.forceUpdate();
  }

  unmountJitto() {
    if (this.jittoHandle) this.jittoHandle.unmount();
    this.jittoHandle = null;
  }

  componentWillUnmount() {
    this.unmountJitto();
  }

  renderDashboard(domRef, dashboardId) {
    // Always one currency: no saved currency means SGD, as the picker shows (ANS-1118).
    const filters = [
      {
        columnName: 'Ccy',
        values: [this.props.currency || DEFAULT_CURRENCY],
        operator: RuntimeFilterOp.EQ,
      },
    ];

    const pinboard = new PinboardEmbed(domRef, {
      frameParams: {
        width: '100%',
        height: '100%',
      },
      fullHeight: true,
      pinboardId: dashboardId,
      runtimeFilters: filters,
      customizations: {
        content: {
          strings: SPOTTER_TEXT_CUSTOMIZATION,
        },
        iconSpriteUrl: SPOTTER_ICON_SPRITE_URL,
      },
    });
    pinboard.render();
  }

  // A pick goes to the seam (the dashboard) and is saved as the preference.
  chooseCurrency(currency) {
    this.currencyFilter.pick(currency);
    this.props.savePreferences({ currency });
  }

  renderCurrencySelector() {
    return (
      <InlineDropdown className={this.props.className} direction="left">
        <span className="pr-2">Currency:</span>
        <DropdownToggle
          disabled={this.pickerBusy()}
          color={this.props.status === 'error' ? 'danger' : 'seconday'}
          value={this.currencyFilter.picker()}
        >
          {this.currencyFilter.picker()}
        </DropdownToggle>
        <DropdownMenu aria-labelledby="navbarDropdownMenuLink">
          {' '}
          {config.fxCurrencies &&
            config.fxCurrencies.map((currency) => (
              <DropdownItem
                key={currency}
                onClick={(e) => {
                  this.chooseCurrency(currency);
                }}
              >
                {currency}
              </DropdownItem>
            ))}
        </DropdownMenu>
      </InlineDropdown>
    );
  }

  // A load or save of the preferences that failed or timed out (ANS-1118).
  renderPreferencesProblem() {
    const { loaded, requests } = this.props;
    let problem = null;
    if (requests && requests.saveFailed) {
      problem = 'Your currency could not be saved.';
    } else if (this.preferencesLoaded() && loaded.failed) {
      problem = 'Your saved currency could not be loaded.';
    }
    if (!problem) return null;
    return (
      <span className="text-danger pr-3 align-self-center" data-testid="preferences-problem">
        {problem}
      </span>
    );
  }

  render() {
    const { viz_id } = this.props.match.params;
    const viz_name =
      lookupDashboardName(this.props.dashboards, viz_id) || 'Loading...';
    const titles = dashboardTitles(viz_name);
    return (
      <>
        <Helmet>
          <title>{titles.tab}</title>
          <meta name="description" content="Description of DashboardPage" />
        </Helmet>
        <Header
          onAssetClassSelected={(assetClass) => {
            this.props.savePreferences({ assetClass: assetClass.id });
          }}
        />
        <h4 className="ml-5 mb-4">{titles.heading}</h4>
        <Row className="justify-content-sm-end mr-4 mb-4">
          {/* With Jitto on, only once the SDK applies host filters (ANS-801):
              until then a pick could not change the dashboard (ANS-795). Nor
              for a dashboard with no Ccy filter: its seam has no mount filters. */}
          {this.renderPreferencesProblem()}
          {(config.jittoHostFilters || !config.jittoEmbedActiveFlag) &&
            isCurrencySelector(this.props.dashboards, viz_id) &&
            this.currencyFilter.mount &&
            this.renderCurrencySelector()}
        </Row>
        <Row>
          <Col>
            {!config.jittoEmbedActiveFlag ? (
              <IFrameContainer ref={this.tsContainerRef} />
            ) : this.jittoEmbedId() ? (
              <>
                {this.currencyFilter.failed() ? (
                  <p className="ml-5" data-testid="jitto-dashboard-failed">
                    This dashboard could not be loaded. Please try again later.
                  </p>
                ) : null}
                <JittoContainer
                  ref={this.jittoContainerRef}
                  data-testid="jitto-dashboard"
                  title={titles.frame}
                />
              </>
            ) : (
              <p className="ml-5" data-testid="jitto-dashboard-unavailable">
                This dashboard could not be loaded. Please try again later.
              </p>
            )}
          </Col>
        </Row>
        <Footer />
      </>
    );
  }
}

function isCurrencySelector(dashboards, vizId) {
  if (!dashboards) return false;
  return dashboards.filter((db) => db.id === vizId)[0];
}

function lookupDashboardName(dashboards, vizId) {
  if (!dashboards) return null;
  const db = dashboards && dashboards.filter((db) => db.id === vizId)[0];
  return db && db.name;
}

export default connect((globalState) => globalState.dashboards, {
  getDashboards: () => ({
    type: 'dashboards/getdashboards',
  }),
})(
  connect((globalState) => globalState.preferences, {
    getPreferences: () => ({
      type: 'preferences/get',
    }),
    savePreferences: (preferences) => ({
      type: 'preferences/save',
      payload: preferences,
    }),
  })(withAuthentication(withRouter(injectIntl(DashboardPage)))),
);
